import { formatDateAsDayOfWeekMonthDayYearString } from "@/helpers/dateTimeUtilities";
import { type LabelService, useLabelService } from "@/composables/label-service";

const labelService: LabelService = useLabelService();

/**
 * Formats a number as a localized percentage string.
 * @param {number|null|undefined} value - The number to format as a percentage.
 * @returns {string} The formatted percentage string, or '--' if the value is null or undefined.
 */
export function formatLocalePercentage( value: number | null | undefined ): string {
    if ( value !== null && value !== undefined ) {
        const percentage: number = value / 100;

        return percentage.toLocaleString( 'en', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    return '--';
}

/**
 * Formats a pre-divided number as a localized percentage string.
 * @param {number|null|undefined} value - The number to format as a percentage.
 * @returns {string} The formatted percentage string, or '--' if the value is null or undefined.
 */
export function formatLocaleDividedPercentage( value: number | null | undefined ): string {
    if (value !== null && value !== undefined) {
        return value.toLocaleString('en', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    return '--';
}

function sanitizeInputToNumber( raw: string | null | undefined ): number {
    const cleaned = String( raw ).replace( /[^0-9.]/g, '' ).trim();
    return parseFloat( cleaned );
}

export function sanitizeInputToFloatingPointNumber( raw: string | null | undefined ): number {
    const floatingPointNumber = sanitizeInputToNumber( raw );
    if ( isNaN( floatingPointNumber ) ) {
        return 0.0;
    }

    return floatingPointNumber;
}

/**
 * Takes any string, removes any non-digits (and '.') characters, attempts to parse it into a floating point number
 * and then formats it as a fixed number of decimal places.
 */
export function sanitizeInputToStringOfValidFloatingPointNumber( raw: string | null | undefined ): string {
    const floatingPointNumber = sanitizeInputToNumber( raw );
    if ( isNaN( floatingPointNumber ) ) {
        return '';
    }

    return formatAsDecimalNumber( floatingPointNumber );
}

/**
 * Takes a number or null/undefined value and returns a valid integer number (default = 0).
 *
 * @param {number | null | undefined} val - the value to format
 * @returns {string} - the truncated value (whole number) formatted string for the passed in value.
 */
export function formatAsWholeNumber( val: number | null | undefined ): string {
    if ( !val ) {
        return '0';
    }

    return new Intl.NumberFormat( 'en' ).format( Math.floor( val ) );
}

/**
 * Takes a number and returns a valid decimal number or empty string ('').
 *
 * @param {number | null | undefined} val - the value to format
 * @param {number} numberDecimalPlaces - the number of decimal values to display
 * @returns {string} - the number value formatted as a string for the passed in parameters or empty string ('').
 */
export function formatAsDecimalNumber( val: number | null | undefined, numberDecimalPlaces: number = 2 ): string {
    if ( !val ) {
        return '';
    }

    return val.toFixed( numberDecimalPlaces );
}

/**
 * Takes a number of null/undefined value and returns a valid currency string.
 *
 * @param {number | null | undefined} val - the price value to format
 * @param {string} invalidResponseToken - the string token that is returned when the value is not able to be parsed
 * @returns {string} - the currency formatted string for the passed in value.
 */
export function formatPriceAsCurrency( val: number | null | undefined, invalidResponseToken?: string | null ): string {
    if ( null == val ) {
        return invalidResponseToken ?? '-';
    }

    return new Intl.NumberFormat( 'en', { style: 'currency', currency: 'USD' } ).format( val || 0 );
}

/**
 * Color codes the stock/sector status text to either red or blue
 * @param { string | null | undefined } value - The text describing the sentiment of the stock/sector.
 * @param { string | null | undefined } bullishColor - (optional) A color to use as an override for the resulting bullish color of the stock/sector. Defaults to 'blue'
 * @param { string | null | undefined } bearishColor - (optional) A color to use as an override for the resulting bearish color of the stock/sector. Defaults to 'red'
 * @param { string | null | undefined } indeterminateColor - (optional) A color to use as an override for the resulting unknown color of the stock/sector. Defaults to 'black'
 * @returns { string } color the text will appear as.
 */
export function generateStatusColor( value: string | null | undefined, bullishColor: string = 'blue', bearishColor: string = 'red', indeterminateColor: string | null = null ): string {
    if ( value ) {
        value = value?.toLowerCase();
        if ( value === 'strong' || value === 'buy' || value === 'x' || value.includes( 'bull' ) || value.includes( 'yes' ) || value.includes( 'buy now' ) ) {
            return bullishColor;
        } else if ( value === 'weak' || value === 'sell' || value === 'o' || value.includes( 'bear' ) || value.includes( 'no' ) || value.includes( 'wait' ) ) {
            return bearishColor;
        } else if ( !isNaN( parseFloat( value ) ) ) {
            const numberValue = parseFloat( value );
            return numberValue > 0 ? bullishColor : bearishColor;
        }
    }

    return indeterminateColor ?? 'black';
}

// Bullish Statuses
export const BullAlert = 'Bull Alert';
export const BullConfirmed = 'Bull Confirmed';
export const BearCorrection = 'Bear Correction';

// Bearish Statuses
export const BullCorrection = 'Bull Correction';
export const BearAlert = 'Bear Alert';
export const BearConfirmed = 'Bear Confirmed';
export const BullTop = 'Bull Top';

export function statusChangeIsBullish( value: string ) {
    return value === BullAlert || value === BullConfirmed || value === BearCorrection;
}

const bullishColor: string = 'text-blue-500';
const bearishColor: string = 'text-red-500';
export function generateBpiStatusColorClass( value: string | undefined | null ) {
    let statusColorClass = 'text-base-content';
    if ( value ) {
        statusColorClass = statusChangeIsBullish( value ) ? bullishColor : bearishColor;
    }

    return statusColorClass;
}

export function generateSimpleLabel( value: string | null | undefined ): string {
    if ( null == value ) {
        return '---';
    }

    return ( 'x' === value.toLowerCase() ||
            'buy' === value.toLowerCase() ||
            'strong' === value.toLowerCase() ||
            value.toLowerCase().includes( 'bull' ) ) ?
                labelService.label( 'lblBullishLabel' ) :
                labelService.label( 'lblBearishLabel' );
}

export function generateSectorOrMarketRsLabel( value: string | null | undefined ): string {
    if ( null == value ) {
        return '---';
    }

    return ( 'x' === value.toLowerCase() ||
            'buy' === value.toLowerCase() ||
            'strong' === value.toLowerCase() ||
            value.toLowerCase().includes( 'bull' ) ) ?
                labelService.label( 'lblRsBullishLabel' ) :
                labelService.label( 'lblRsBearishLabel' );
}
